<template>
  <v-app-bar
    :clipped-left="$vuetify.breakpoint.lgAndUp"
    app
    color="primary"
    dark
  >
    <v-progress-linear
      :active="isAppLoading"
      :indeterminate="true"
      absolute
      :top="true"
      color="accent"
    ></v-progress-linear>

    <v-toolbar-title style="max-width: 300px">
      <v-app-bar-nav-icon @click.stop="toggleDrawer" />
      <span class="hidden-sm-and-down" v-if="currentUser">
        {{ currentUser.company }}
      </span>
    </v-toolbar-title>
    <v-spacer />

    <v-card class="d-inline-flex pa-2" width="250px" flat color="transparent">
      <v-select
        :items="centerList"
        v-model="selectedCenter"
        menu-props="auto"
        label="Location"
        hide-details
        return-object
        item-text="name"
        item-value="id"
        @change="changeCenter"
        :prepend-icon="
          $vuetify.breakpoint.smAndDown
            ? ''
            : 'mdi-office-building-marker-outline'
        "
      ></v-select>
    </v-card>
    <v-menu v-if="modules && modules.length > 0" offset-y>
      <template v-slot:activator="{ on, attrs }">
        <v-btn tile large icon v-bind="attrs" v-on="on">
          <v-icon>mdi-dots-grid</v-icon>
        </v-btn>
      </template>
      <v-card class="mx-auto" max-width="400" width="300" tile>
        <v-card-text>
          <v-row>
            <v-col
              v-for="module in modules"
              :key="module.id"
              style="text-align: center"
              cols="6"
            >
              <v-hover v-slot="{ hover }">
                <v-sheet
                  @click="toApp(module)"
                  :elevation="hover ? 16 : 0"
                  :class="{ 'on-hover': hover }"
                >
                  <v-icon size="50" :color="module.color">
                    {{ module.icon }}
                  </v-icon>
                  <br />
                  {{ module.name }}
                </v-sheet>
              </v-hover>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-menu>

    <v-btn data-cy="login" to="/login" v-if="!isLoggedIn" icon title="Login">
      <v-icon>mdi-login</v-icon>
    </v-btn>
    <div v-if="currentUser" class="text-center">
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn class="ma-0" tile large icon v-bind="attrs" v-on="on">
            <v-icon>mdi-shield-account</v-icon>
          </v-btn>
        </template>

        <v-card class="min-width: 250px;">
          <v-card-title v-if="currentUser.logo" class="justify-center pb-0">
            <v-avatar size="80">
              <v-img :src="currentUser.logo"></v-img>
            </v-avatar>
          </v-card-title>

          <v-card-title
            class="justify-center text-subtitle-1 mb-2"
            style="white-space: nowrap"
          >
            {{ currentUser.company }}
          </v-card-title>

          <v-card-subtitle class="text-center">
            <v-icon small>mdi-office-building-marker-outline</v-icon>
            {{ globalCenter.name }}
          </v-card-subtitle>
          <v-divider></v-divider>

          <v-card-text class="pa-0">
            <v-list nav dense>
              <v-list-item-group active-class="grey--text text--disabled">
                <v-list-item
                  v-if="availableAccounts.length > 0"
                  @click="openSwitchAccountDialog"
                  :disabled="loadingAccounts"
                >
                  <v-list-item-icon class="mr-2">
                    <v-progress-circular
                      v-if="loadingAccounts"
                      indeterminate
                      size="20"
                      width="2"
                      color="primary"
                    ></v-progress-circular>
                    <v-icon v-else>mdi-account-switch</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ loadingAccounts ? 'Loading...' : 'Switch Account' }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item :to="{ name: 'EditProfile' }">
                  <v-list-item-icon class="mr-2">
                    <v-icon>mdi-account</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ currentUser.firstName }}
                      {{ currentUser.lastName }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item
                  :to="item.link"
                  v-for="(item, i) in userMenu"
                  :key="i"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon>{{ item.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item @click="logout">
                  <v-list-item-icon class="mr-3 default">
                    <v-icon color="primary">mdi-logout</v-icon>
                  </v-list-item-icon>

                  <v-list-item-content>
                    <v-list-item-title>Logout</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-card-text>
        </v-card>
      </v-menu>
    </div>

    <!-- Switch Account Dialog -->
    <v-dialog v-model="switchAccountDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline">
          Switch Account
          <v-spacer></v-spacer>
          <v-progress-circular
            v-if="loadingAccounts"
            indeterminate
            size="24"
            width="2"
            color="primary"
          ></v-progress-circular>
        </v-card-title>

        <v-card-text>
          <div v-if="loadingAccounts" class="text-center py-4">
            <v-progress-circular
              indeterminate
              size="40"
              width="3"
              color="primary"
            ></v-progress-circular>
            <p class="mt-3 text-body-2 grey--text">
              Loading available accounts...
            </p>
          </div>

          <v-radio-group v-else v-model="selectedAccount">
            <v-radio
              v-for="account in availableAccounts"
              :key="account.id"
              :label="account.name"
              :value="account.id"
              :disabled="switchingAccount"
            >
              <template v-slot:label>
                <div>
                  <div>{{ account.name }}</div>
                  <div class="text-caption grey--text">{{ account.email }}</div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>

          <div v-if="switchingAccount" class="text-center py-4">
            <v-progress-circular
              indeterminate
              size="40"
              width="3"
              color="primary"
            ></v-progress-circular>
            <p class="mt-3 text-body-2 grey--text">Switching account...</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="switchAccountDialog = false"
            :disabled="loadingAccounts || switchingAccount"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!selectedAccount || loadingAccounts || switchingAccount"
            :loading="switchingAccount"
            @click="switchAcct(selectedAccount)"
          >
            Switch
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app-bar>
</template>

<script>
import { parseDomain } from 'parse-domain';
import { mapActions, mapGetters } from 'vuex';

export default {
  data: () => ({
    selectedCenter: null,
    centerList: [],
    drawer: null,
    menuItem: 1,
    userMenu: [
      // {
      //   text: "My Home",
      //   link: "/secure/myhome",
      //   icon: "mdi-account",
      // },
    ],
    modules: [],
    switchAccountDialog: false,
    availableAccounts: [],
    selectedAccount: null,
    loadingAccounts: false,
    switchingAccount: false,
  }),
  computed: {
    ...mapGetters('auth', ['isLoggedIn', 'currentUser', 'isAppLoading']),
    ...mapGetters('navigation', ['isAppLoading', 'globalCenter']),
  },
  async mounted() {
    //check jwt token all each page reload
    await this.remoteJwtTokenCheck();
    await this.fetchAvailableAccounts();

    if (!this.currentUser) return;

    this.initCenters()
      .then((centers) => {
        this.centerList = centers;
        if (this.centerList && this.centerList.length > 1) {
          this.centerList.unshift({
            id: null,
            name: 'All Locations',
            accountId: this.centerList[0].accountId,
          });
        }
        this.setMyCenters(centers);

        this.selectedCenter = this.globalCenter;
        if (centers && centers.length > 0) {
          const existIndex = centers.findIndex(
            (c) => this.selectedCenter && c.id == this.selectedCenter.id,
          );
          if (!existIndex || existIndex < 0) {
            this.selectedCenter = centers[0];
            this.setCenter(this.selectedCenter);
          }
        }
      })
      .finally(() => {
        this.$emit('load', this.selectedCenter);
      });

    this.initModules().then((modules) => {
      if (modules) {
        this.modules = modules.filter((m) => m.path !== 'reports');
      }
    });
  },
  methods: {
    ...mapActions('navigation', [
      'toggleDrawer',
      'hideDrawer',
      'setCenter',
      'clearCenter',
    ]),
    ...mapActions('auth', [
      'processLogout',
      'remoteJwtTokenCheck',
      'setMyCenters',
    ]),
    ...mapActions('messages', ['addErrorMessage']),
    logout() {
      this.$axios.post('gaa-user/signout').then(() => {
        this.processLogout();
        this.clearCenter();
        delete this.$axios.defaults.headers.common['Authorization'];
        this.$router.push({ name: 'Login' });
      });
    },
    changeCenter() {
      this.setCenter(this.selectedCenter);
      window.location.reload();
    },
    switchAcct(accountId) {
      this.switchingAccount = true;
      this.$axios
        .post('/gaa-user/switch-account', null, { params: { accountId } })
        .then(() => {
          // Refresh user data instead of full reload
          return this.remoteJwtTokenCheck();
        })
        .then(() => {
          // Update navigation and centers
          this.initCenters().then((centers) => {
            this.centerList = centers;
            if (this.centerList && this.centerList.length > 1) {
              this.centerList.unshift({
                id: null,
                name: 'All Locations',
                accountId: this.centerList[0].accountId,
              });
            }
            this.setMyCenters(centers);
            this.selectedCenter = centers[0] || this.globalCenter;
            this.setCenter(this.selectedCenter);
            this.$emit('load', this.selectedCenter);
            // Reload the page after successful account switch
            window.location.reload();
          });
        })
        .catch((error) => {
          this.switchingAccount = false;
          this.addErrorMessage(error);
          this.$router.push('/login');
        });
    },
    async openSwitchAccountDialog() {
      this.loadingAccounts = true;
      this.switchAccountDialog = true;

      try {
        // Fetch JWT token data
        const response = await this.$axios.get('/jwt-token-check');

        if (response.data && response.data.availableAccounts) {
          this.availableAccounts = response.data.availableAccounts.map(
            (account) => ({
              id: account.id,
              name: account.name,
              email: response.data.email,
            }),
          );
          this.availableAccounts.sort((a, b) => a.name.localeCompare(b.name));
        } else {
          this.availableAccounts = [];
        }
      } catch (error) {
        console.error('Error fetching JWT data:', error);
        this.availableAccounts = [];
        this.addErrorMessage(error);
      } finally {
        this.loadingAccounts = false;
      }

      this.selectedAccount = null;
    },
    async initCenters() {
      return await this.$axios.get('/center/get-my-centers').then((resp) => {
        const centers = resp.data;

        if (centers) {
          centers.sort((c1, c2) => (c1.name > c2.name ? 1 : -1));
        }
        return centers;
      });
    },
    async initModules() {
      return await this.$axios
        .get('/user/module', { params: { user_id: this.currentUser.uId } })
        .then((resp) => {
          return resp.data;
        });
    },
    toApp(app) {
      if (process.env.NODE_ENV != 'development') {
        const parseResult = parseDomain(window.location.hostname);
        const { domain, topLevelDomains } = parseResult;
        const topLevel = topLevelDomains.join('.');

        const moduleUrl = `https://${app.path}.${domain}.${topLevel}`;
        window.location.href = moduleUrl;
      }
    },
    async fetchAvailableAccounts() {
      this.loadingAccounts = true;
      try {
        const response = await this.$axios.get('/jwt-token-check');
        if (response.data && response.data.availableAccounts) {
          this.availableAccounts = response.data.availableAccounts.map(
            (account) => ({
              id: account.id,
              name: account.name,
              email: response.data.email,
            }),
          );
          this.availableAccounts.sort((a, b) => a.name.localeCompare(b.name));
        } else {
          this.availableAccounts = [];
        }
      } catch (error) {
        this.availableAccounts = [];
        this.addErrorMessage(error);
      } finally {
        this.loadingAccounts = false;
      }
    },
  },
};
</script>

<style></style>
